import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import SearchTable from '../../../../components/search/SearchTable';
import { buildQuickFilterOptions, setAdvancedFilterValues } from '../../../../components/search/util';
import CompanySettingTypes from '../../../../constants/companySettings';
import { Statuses } from '../../../../constants/statuses';
import { filterOperators } from '../../../../redux/blueprints/search';
import {
  selectCompanyCodes, selectCompanyDepartments,
  selectCompanyDocumentTypes,
  selectCompanyFeatures,
  selectCompanySettingValue,
  selectCompanyUsers
} from '../../../../redux/modules/company';
import { insuredSearch } from '../../../../redux/modules/insureds';
import { getReportsStats } from '../../../../redux/modules/reports';
import { createMap, modifyArrayItemAt, removeArrayItemAt } from '../../../../utils/arrayUtil';
import { createHeaderCell } from '../../../../utils/tableUtil';

import advancedFilters from './advancedFilters';
import columns, { ColumnTypes } from './columns';
import quickFilters from './quickFilters';

const defaultColumnNames = [
  ColumnTypes.Checkbox,
  ColumnTypes.Insured,
  ColumnTypes.ExternalId,
  ColumnTypes.ParentId,
  ColumnTypes.JobInfo,
  ColumnTypes.Items,
  ColumnTypes.LatestEmail
];

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ReportTable = () => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const query = useQuery();
  const departmentId = parseInt(query.get('department_id'), 10);

  const companyDepartments = useSelector(selectCompanyDepartments);
  const companyCodes = useSelector(selectCompanyCodes);
  const companyUsers = useSelector(selectCompanyUsers);
  const companyDocumentTypes = useSelector(selectCompanyDocumentTypes);
  let hiddenColumns = useSelector(insuredSearch.selectHiddenColumnIds);
  const columnsToggled = useSelector(insuredSearch.selectColumnsToggled);
  const companyFeatures = useSelector(selectCompanyFeatures);
  const hideNonPolicyHolders = useSelector(selectCompanySettingValue(CompanySettingTypes.ReportsHideNonPolicyHolders));
  const companyFeaturesMap = useMemo(() => createMap(companyFeatures, 'name'), [companyFeatures]);

  let columnsWithConditionalColumns = columns
    .filter(column => (companyFeaturesMap[column?.flag] ? companyFeaturesMap[column?.flag].isEnabled : true));

  columnsWithConditionalColumns = !hideNonPolicyHolders
    ? removeArrayItemAt(columnsWithConditionalColumns, 'name', ColumnTypes.ParentId)
    : columnsWithConditionalColumns;

  if (!hiddenColumns?.length && !columnsToggled) {
    hiddenColumns = columnsWithConditionalColumns
      .filter(c => !c.disableToggle && !defaultColumnNames.includes(c.name))
      .map(c => c.id || c.accessor);
  }

  useEffect(() => {
    const filterBuild = buildQuickFilterOptions(quickFilters, {
      departmentName: companyDepartments,
      codeApplied: companyCodes
    }, { departmentName: true });

    dispatch(insuredSearch.setQuickFilterOptions(filterBuild));

    dispatch(insuredSearch.updateQuickFilters(
      'departmentName',
      companyDepartments.filter(dept => dept.departmentId === departmentId)
        .map(dept => ({
          departmentId: dept.departmentId,
          departmentName: dept.departmentName
        }))
    ));

    const companyCodeOptions = companyCodes.map(({ letter }) => ({
      text: letter,
      value: letter
    }));

    const companyUserOptions = companyUsers?.map(({ userId, userEmail }) => ({
      text: userEmail,
      value: userId
    })) || [];

    dispatch(insuredSearch.setAdvancedFilterOptions(setAdvancedFilterValues(advancedFilters, {
      codes: { subProperty: 'letter', values: companyCodeOptions },
      projects: { subProperty: 'owner', values: companyUserOptions },
      documents: { subProperty: 'requirement type', values: companyDocumentTypes }
    })));

    dispatch(insuredSearch.setBaseFilters([{ key: 'company.id', value: companyId }, { key: 'status', value: [Statuses.Approved, Statuses.NotApproved] }]));
    dispatch(insuredSearch.setColumns(columnsWithConditionalColumns));
    dispatch(insuredSearch.setHiddenColumnIds(hiddenColumns));

    if (hideNonPolicyHolders) {
      dispatch(insuredSearch.setDefaultAdvancedFilters({
        value: '1970-01-01',
        key: 'insurance_policies.effective_at',
        operator: filterOperators.isAfter,
        type: 'date'
      }));

      dispatch(insuredSearch.setScriptScore({
        source: `
          doc['insurance_requirement.parent_type.keyword'].value === 'insured'
            ? 3
            : !doc['item.codes.id'].empty
                ? 2
                : !doc['insurance_policies.id'].empty
                    ? 1
                    : 0`
      }));
    }

    dispatch(insuredSearch.getPage());
    dispatch(getReportsStats([companyId]));
  }, []);

  return <SearchTable heightMultiplier={200} searchBlueprint={insuredSearch} />;
};

export default ReportTable;
